import { useState } from "react";
import axios from "axios";
import { Button } from "../../../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../../../components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../../components/ui/select";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import { toast } from "sonner";
import { FileText, Download, Loader2, Users, Calendar, CheckCircle2 } from "lucide-react";
import { ExportToolbar } from "../../../components/ui/export-toolbar";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PERIODS = [
  { value: "this_week",  label: "This Week" },
  { value: "last_week",  label: "Last Week" },
  { value: "this_month", label: "This Month" },
  { value: "last_month", label: "Last Month" },
  { value: "ytd",        label: "Year to Date" },
  { value: "custom",     label: "Custom" },
];

export default function SummaryRecruitersReportPage() {
  const [period, setPeriod] = useState("this_month");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [loading, setLoading] = useState(false);
  const [downloadingPdf, setDownloadingPdf] = useState(false);
  const [downloadingZip, setDownloadingZip] = useState(false);
  const [reportData, setReportData] = useState(null);
  const [teamsSummary, setTeamsSummary] = useState(false);

  const buildPayload = () => {
    const payload = { period, teams_summary: teamsSummary };
    if (period === "custom") {
      if (!startDate || !endDate) {
        toast.error("Please select both start and end dates");
        return null;
      }
      payload.start_date = startDate;
      payload.end_date = endDate;
    }
    return payload;
  };

  const generateReport = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/summary-recruiters-report/data`, payload);
      setReportData(res.data);
      const hasData = res.data.teams_summary
        ? res.data.summary_rows && res.data.summary_rows.length > 0
        : res.data.teams && res.data.teams.length > 0;
      if (!hasData) {
        toast.info("No data found for the selected period");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to generate report");
    } finally {
      setLoading(false);
    }
  };

  const downloadPdf = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setDownloadingPdf(true);
    try {
      const res = await axios.post(`${API}/admin/summary-recruiters-report/pdf`, payload);
      const byteCharacters = atob(res.data.pdf_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/pdf" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.data.filename || "SummaryRecruitersReport.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("PDF downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to download PDF");
    } finally {
      setDownloadingPdf(false);
    }
  };

  const downloadZip = async () => {
    const payload = buildPayload();
    if (!payload) return;
    setDownloadingZip(true);
    try {
      const res = await axios.post(`${API}/admin/summary-recruiters-report/zip`, payload);
      const byteCharacters = atob(res.data.zip_data);
      const byteNumbers = new Array(byteCharacters.length);
      for (let i = 0; i < byteCharacters.length; i++) {
        byteNumbers[i] = byteCharacters.charCodeAt(i);
      }
      const byteArray = new Uint8Array(byteNumbers);
      const blob = new Blob([byteArray], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = res.data.filename || "SummaryRecruitersReport.zip";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success("ZIP downloaded");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to download ZIP");
    } finally {
      setDownloadingZip(false);
    }
  };

  const fields = reportData?.form_fields || [];
  const teams = reportData?.teams || [];
  const summaryRows = reportData?.summary_rows || [];
  const isTeamsSummary = reportData?.teams_summary || false;
  const hasAvgSub = reportData?.has_avg_sub || false;
  const hasJd = reportData?.has_jd_data || false;
  const periodLabel = PERIODS.find((p) => p.value === period)?.label || period;

  // JD field mapping: self-reported field id → JD key
  const jdMap = { num_submittals: { key: 'jd_submittals', label: 'JD Sub' }, num_interviews: { key: 'jd_interviews', label: 'JD Int' }, num_new_starts: { key: 'jd_hires', label: 'JD Hire' } };
  const jdColCount = hasJd ? fields.filter(f => jdMap[f.id]).length : 0;
  const totalColSpan = fields.length + jdColCount + (hasAvgSub ? 3 : 2);
  const hasData = isTeamsSummary ? summaryRows.length > 0 : teams.length > 0;

  const fmtVal = (val, isAvg) => {
    if (isAvg) return Number(val || 0).toFixed(2);
    const n = Number(val || 0);
    return n === Math.floor(n) ? String(Math.floor(n)) : n.toFixed(2);
  };

  // Flatten data into rows for ExportToolbar
  const exportColumns = isTeamsSummary
    ? [
        { field: "team_leader", headerName: "Team Leader" },
        ...fields.flatMap((f) => {
          const label = f.label + (hasJd && jdMap[f.id] ? ' (Log)' : '');
          const cols = [{ field: f.id, headerName: label, format: f.is_average ? (v) => Number(v || 0).toFixed(2) : undefined }];
          if (hasJd && jdMap[f.id]) cols.push({ field: jdMap[f.id].key, headerName: jdMap[f.id].label });
          return cols;
        }),
        { field: "missing_count", headerName: "Missing Days" },
      ]
    : [
        { field: "team_leader", headerName: "Team Leader" },
        { field: "user_name", headerName: "User Name" },
        ...fields.flatMap((f) => {
          const label = f.label + (hasJd && jdMap[f.id] ? ' (Log)' : '');
          const cols = [{ field: f.id, headerName: label, format: f.is_average ? (v) => Number(v || 0).toFixed(2) : undefined }];
          if (hasJd && jdMap[f.id]) cols.push({ field: jdMap[f.id].key, headerName: jdMap[f.id].label });
          return cols;
        }),
        { field: "missing_count", headerName: "Missing Days" },
      ];

  const exportRows = [];
  if (isTeamsSummary) {
    for (const r of summaryRows) {
      const row = { team_leader: r.team_leader_name, missing_count: r.missing_count || 0 };
      for (const f of fields) row[f.id] = r.totals?.[f.id] ?? 0;
      if (hasJd) { row.jd_submittals = r.jd_submittals ?? '-'; row.jd_interviews = r.jd_interviews ?? '-'; row.jd_hires = r.jd_hires ?? '-'; }
      exportRows.push(row);
    }
  } else {
    for (const team of teams) {
      for (const member of (team.members || [])) {
        const row = { team_leader: team.team_leader?.name || "Unassigned", user_name: member.user_name, missing_count: member.missing_count || 0 };
        for (const f of fields) row[f.id] = member.totals?.[f.id] ?? 0;
        if (hasJd) { row.jd_submittals = member.jd_submittals ?? '-'; row.jd_interviews = member.jd_interviews ?? '-'; row.jd_hires = member.jd_hires ?? '-'; }
        exportRows.push(row);
      }
    }
  }

  return (
    <div className="space-y-6 animate-fade-in">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div>
          <h1
            className="text-2xl font-bold tracking-tight text-slate-900 flex items-center gap-2"
            style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }}
          >
            <Users className="h-6 w-6 text-indigo-600" />
            Summary Recruiters Report
          </h1>
          <p className="text-slate-500 text-sm mt-1">
            Period totals for all recruiters, grouped by team leader
          </p>
        </div>
        <ExportToolbar
          title="Summary Recruiters Report"
          rows={exportRows}
          columns={exportColumns}
          periodLabel={reportData ? `${reportData.period_label}: ${reportData.start_date} to ${reportData.end_date}` : ""}
          filenamePrefix="SummaryRecruitersReport"
          reportType="summary-recruiters"
        />
      </div>

      {/* Controls */}
      <Card>
        <CardContent className="pt-4 pb-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-1.5 min-w-[180px]">
              <Label className="text-xs text-slate-500 uppercase tracking-wide">
                <Calendar className="h-3.5 w-3.5 inline mr-1" />
                Period
              </Label>
              <Select value={period} onValueChange={setPeriod}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {PERIODS.map((p) => (
                    <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {period === "custom" && (
              <>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-500 uppercase tracking-wide">Start Date</Label>
                  <Input type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} className="w-[160px]" />
                </div>
                <div className="space-y-1.5">
                  <Label className="text-xs text-slate-500 uppercase tracking-wide">End Date</Label>
                  <Input type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} className="w-[160px]" />
                </div>
              </>
            )}

            {/* Teams Summary checkbox */}
            <label className="flex items-center gap-2 cursor-pointer select-none">
              <input
                type="checkbox"
                checked={teamsSummary}
                onChange={(e) => setTeamsSummary(e.target.checked)}
                className="h-4 w-4 rounded border-slate-300 text-indigo-600 focus:ring-indigo-500"
              />
              <span className="text-sm text-slate-700 font-medium">Teams Summary</span>
            </label>

            <Button onClick={generateReport} disabled={loading} className="gap-1.5">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4" />}
              Generate Report
            </Button>

            {hasData && (
              <>
                <Button variant="outline" onClick={downloadPdf} disabled={downloadingPdf} className="gap-1.5">
                  {downloadingPdf ? <Loader2 className="h-4 w-4 animate-spin" /> : <FileText className="h-4 w-4 text-red-500" />}
                  Download PDF
                </Button>
                <Button variant="outline" onClick={downloadZip} disabled={downloadingZip} className="gap-1.5">
                  {downloadingZip ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4 text-blue-500" />}
                  Download ZIP
                </Button>
              </>
            )}
          </div>
        </CardContent>
      </Card>

      {/* Report Table */}
      {loading ? (
        <Card>
          <CardContent className="py-16">
            <div className="flex items-center justify-center gap-3 text-slate-400">
              <Loader2 className="h-5 w-5 animate-spin" />
              Generating report...
            </div>
          </CardContent>
        </Card>
      ) : reportData && !hasData ? (
        <Card>
          <CardContent className="py-16 text-center text-slate-400">
            <Users className="h-10 w-10 mx-auto mb-3 text-slate-300" />
            <p className="text-sm">No data found for the selected period</p>
          </CardContent>
        </Card>
      ) : hasData ? (
        <Card>
          <CardHeader className="pb-2 flex flex-row items-center justify-between">
            <CardTitle className="text-base">{reportData.period_label} {isTeamsSummary ? "Teams Summary" : "Summary"}</CardTitle>
            <span className="text-xs text-slate-400">
              {reportData.start_date} &rarr; {reportData.end_date} &nbsp;|&nbsp; {reportData.working_days} working days
            </span>
          </CardHeader>
          <CardContent className="p-0">
            <div className="overflow-x-auto">

              {/* ── Teams Summary: flat table, one row per team ── */}
              {isTeamsSummary ? (
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-blue-700 text-white text-xs font-medium">
                      <th className="px-3 py-2 text-left">Team Leader</th>
                      {fields.flatMap((f) => {
                        const cols = [<th key={f.id} className="px-3 py-2 text-right">{f.label}{hasJd && jdMap[f.id] ? ' (Log)' : ''}</th>];
                        if (hasJd && jdMap[f.id]) cols.push(<th key={`jd-${f.id}`} className="px-3 py-2 text-right bg-indigo-800">{jdMap[f.id].label}</th>);
                        return cols;
                      })}
                      <th className="px-3 py-2 text-right">Missing Days</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summaryRows.map((row, idx) => (
                      <tr key={idx} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                        <td className="px-3 py-2 text-slate-800 font-medium">{row.team_leader_name}</td>
                        {fields.flatMap((f) => {
                          const cells = [<td key={f.id} className="px-3 py-2 text-right text-slate-700">{fmtVal(row.totals?.[f.id], f.is_average)}</td>];
                          if (hasJd && jdMap[f.id]) cells.push(<td key={`jd-${f.id}`} className="px-3 py-2 text-right text-indigo-700 font-medium">{row[jdMap[f.id].key] ?? '-'}</td>);
                          return cells;
                        })}
                        <td className="px-3 py-2 text-right">
                          {(row.missing_count || 0) > 0 ? (
                            <span className="inline-flex items-center justify-center bg-red-100 text-red-700 text-xs font-bold rounded-full px-2 py-0.5 min-w-[24px]">
                              {row.missing_count}
                            </span>
                          ) : (
                            <CheckCircle2 className="h-4 w-4 text-green-500 inline-block" />
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr className="bg-emerald-50 font-bold text-emerald-900 border-t-2 border-emerald-200">
                      <td className="px-3 py-2">Grand Total</td>
                      {fields.flatMap((f) => {
                        const cells = [<td key={f.id} className="px-3 py-2 text-right">{f.is_average ? `Avg: ${Number(reportData.grand_totals?.[f.id] ?? 0).toFixed(2)}` : fmtVal(reportData.grand_totals?.[f.id], false)}</td>];
                        if (hasJd && jdMap[f.id]) cells.push(<td key={`jd-${f.id}`} className="px-3 py-2 text-right text-indigo-800">{summaryRows.reduce((s, r) => s + (r[jdMap[f.id].key] || 0), 0)}</td>);
                        return cells;
                      })}
                      <td className="px-3 py-2 text-right" />
                    </tr>
                  </tfoot>
                </table>

              ) : (
                /* ── Detail view: grouped by team leader ── */
                <table className="w-full text-sm">
                  {teams.map((team, teamIdx) => (
                    <tbody key={team.team_leader?.id || teamIdx}>
                      <tr>
                        <td colSpan={totalColSpan} className="bg-slate-800 text-white font-bold px-3 py-2 text-sm">
                          {team.team_name} — {team.team_leader?.name || "Unassigned"} ({team.members?.length || 0} members)
                        </td>
                      </tr>
                      <tr className="bg-blue-700 text-white text-xs font-medium">
                        <th className="px-3 py-2 text-left">User Name</th>
                        {fields.flatMap((f) => {
                          const cols = [<th key={f.id} className="px-3 py-2 text-right">{f.label}{hasJd && jdMap[f.id] ? ' (Log)' : ''}</th>];
                          if (hasJd && jdMap[f.id]) cols.push(<th key={`jd-${f.id}`} className="px-3 py-2 text-right bg-indigo-800">{jdMap[f.id].label}</th>);
                          return cols;
                        })}
                        <th className="px-3 py-2 text-right">Missing Days</th>
                      </tr>
                      {team.members?.map((member, idx) => (
                        <tr key={member.user_id || idx} className={idx % 2 === 0 ? "bg-white" : "bg-slate-50"}>
                          <td className="px-3 py-2 text-slate-800 font-medium">{member.user_name}</td>
                          {fields.flatMap((f) => {
                            const cells = [<td key={f.id} className="px-3 py-2 text-right text-slate-700">{fmtVal(member.totals?.[f.id], f.is_average)}</td>];
                            if (hasJd && jdMap[f.id]) cells.push(<td key={`jd-${f.id}`} className="px-3 py-2 text-right text-indigo-700 font-medium">{member[jdMap[f.id].key] ?? '-'}</td>);
                            return cells;
                          })}
                          <td className="px-3 py-2 text-right">
                            {member.missing_count > 0 ? (
                              <span className="inline-flex items-center justify-center bg-red-100 text-red-700 text-xs font-bold rounded-full px-2 py-0.5 min-w-[24px]" title={member.missing_days?.join(", ")}>
                                {member.missing_count}
                              </span>
                            ) : (
                              <CheckCircle2 className="h-4 w-4 text-green-500 inline-block" />
                            )}
                          </td>
                        </tr>
                      ))}
                      <tr className="bg-blue-50 font-bold text-blue-900">
                        <td className="px-3 py-2">Team Total</td>
                        {fields.flatMap((f) => {
                          const cells = [<td key={f.id} className="px-3 py-2 text-right">{f.is_average ? `Avg: ${Number(team.team_totals?.[f.id] ?? 0).toFixed(2)}` : fmtVal(team.team_totals?.[f.id], false)}</td>];
                          if (hasJd && jdMap[f.id]) cells.push(<td key={`jd-${f.id}`} className="px-3 py-2 text-right text-indigo-800">{(team.members || []).reduce((s, m) => s + (m[jdMap[f.id].key] || 0), 0)}</td>);
                          return cells;
                        })}
                        <td className="px-3 py-2 text-right" />
                      </tr>
                      {teamIdx < teams.length - 1 && (
                        <tr><td colSpan={fields.length + 2} className="h-3" /></tr>
                      )}
                    </tbody>
                  ))}
                  <tfoot>
                    <tr className="bg-emerald-50 font-bold text-emerald-900 border-t-2 border-emerald-200">
                      <td className="px-3 py-2">Grand Total</td>
                      {fields.flatMap((f) => {
                        const cells = [<td key={f.id} className="px-3 py-2 text-right">{f.is_average ? `Avg: ${Number(reportData.grand_totals?.[f.id] ?? 0).toFixed(2)}` : fmtVal(reportData.grand_totals?.[f.id], false)}</td>];
                        if (hasJd && jdMap[f.id]) cells.push(<td key={`jd-${f.id}`} className="px-3 py-2 text-right text-indigo-800">{teams.flatMap(t => t.members || []).reduce((s, m) => s + (m[jdMap[f.id].key] || 0), 0)}</td>);
                        return cells;
                      })}
                      <td className="px-3 py-2 text-right" />
                    </tr>
                  </tfoot>
                </table>
              )}
            </div>
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}
